import React from 'react';
import type { FormikHelpers, FormikProps } from 'formik';
import { Formik } from 'formik';
import { BucketFormikForm } from 'Src/types/widget-helpers';
import { useAppContext } from 'Context/ReactAppContext';
import { t } from 'Src/i18n';
import { classnames } from 'Utils/classnames/classnames.utils';
import { scrollAndFocusNextInput } from 'Utils/form/form.utils';
import { Button } from 'Components/frameworks/button/Button';
import { Spinner } from 'Components/frameworks/spinner/Spinner';
type Props = {
  actions?: React.ReactNode;
  children: (arg0: {
    focusNextInput: (e: KeyboardEvent) => void;
    formik: FormikProps<Record<string, unknown>>;
    hasUnicode: boolean;
    locale: string;
  }) => React.ReactNode;
  className?: string;
  id: string;
  initialValues?: Record<string, unknown>;
  isLoading?: boolean;
  isSubmiting?: boolean;
  onCancel?: () => void;
  onSubmit: (
    values: Record<string, unknown>,
    bag?: FormikHelpers<Record<string, unknown>>,
  ) => void | Promise<unknown>;
  validationSchema?: unknown;
};
export const EditionForm = ({
  actions,
  children,
  className,
  initialValues,
  id,
  isLoading = false,
  isSubmiting = false,
  onCancel,
  onSubmit,
  validationSchema,
}: Props) => {
  const appContext = useAppContext();
  const { displayUnicodeFields, locale } = appContext;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-20">
        <Spinner size="medium" />
      </div>
    );
  }

  const dataForm = `${id}-from`;

  const focusNextInput = (e: KeyboardEvent) => scrollAndFocusNextInput(e, dataForm);

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={validationSchema}>
      {(formik) => (
        <BucketFormikForm
          className={classnames('relative', className)}
          data-form={dataForm}
          id={id}
        >
          {children({
            focusNextInput,
            formik,
            hasUnicode: displayUnicodeFields,
            locale,
          })}
          {actions ?? (
            <div className="mt-20 flex flex-col justify-center gap-x-20 gap-y-10 sm:flex-row">
              {onCancel && (
                <button
                  className="cursor-pointer text-14 underline transition-color duration-250 ease-in hocus:text-saffron"
                  onClick={onCancel}
                  type="button"
                >
                  {t('common.cancel')}
                </button>
              )}
              <Button type="submit">{t('common.save')}</Button>
            </div>
          )}
          {isSubmiting && (
            <div className="absolute inset-0 flex items-center justify-center bg-white p-20 opacity-75">
              <Spinner size="medium" />
            </div>
          )}
        </BucketFormikForm>
      )}
    </Formik>
  );
};
